import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { filter, take } from 'rxjs';
import { PlainObject } from 'simplytyped';
import { SiteValidatorService, ThemeStore } from '~/shared-common';
import { SeoService } from '~/shared-core/utils';
import { environment } from '../environments/environment';
import { LoginComponent } from './auth/login/login.component';
import { AppLayoutComponent } from './layout/app-layout.component';
import { AuthStore, LocalStorageJwtService } from './stores/auth';
import { ThemeService } from './stores/themes/theme.service';
@Component({
  standalone: true,
  imports: [
    RouterModule,
    AppLayoutComponent,
    CommonModule,
    LoginComponent,
    ToastModule,
  ],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [MessageService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  $title = 'Worktraq 2024';
  private title = inject(Title);
  private seoService = inject(SeoService);
  private readonly messageService = inject(MessageService);
  private readonly localStorageJwtService = inject(LocalStorageJwtService);
  private readonly authStore = inject(AuthStore);
  private readonly themeStore = inject(ThemeStore);
  private readonly themeService = inject(ThemeService);
  private readonly siteValidatorService = inject(SiteValidatorService);
  private readonly destroyRef = inject(DestroyRef);

  $currentUser = this.authStore.user;
  currentUser$ = toObservable(this.$currentUser);
  $error = this.themeStore.error;

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: PlainObject,
  ) {
    effect(() => {
      const error = this.$error();
      if (error !== null) {
        this.messageService.add({
          severity: 'error',
          detail: error.message,
          summary: error.title ?? 'Error',
          life: 5000,
        });
      }
    });

    effect(() => {
      if (this.authStore.tokenExpired()) {
        const hostName = this.siteValidatorService.checkGTPSite();
        if (hostName) {
          this.authStore.b2bLogin({
            data: { hostName, direct: 1 },
          });
        }
      }
    });
  }

  ngOnInit(): void {
    const isApiLoginRoute =
      typeof window !== 'undefined' &&
      (window.location.pathname.includes('/api-login/') ||
        window.location.pathname.includes('/get-api-login/'));

    this.localStorageJwtService
      .getItem()
      .pipe(take(1))
      .subscribe((token) => {
        if (isApiLoginRoute) return;

        if (token) {
          this.authStore.getUser();
        } else if (isPlatformBrowser(this.platformId)) {
          const hostName = this.siteValidatorService.checkGTPSite();
          if (hostName) {
            this.authStore.b2bLogin({
              data: {
                hostName: hostName,
                direct: 0,
              },
            });
          }
        }
      });
    this.currentUser$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((user) => !!user),
      )
      .subscribe((user: any) => {
        const company = user.company;
        const faviconFile = company?.favicon_file;

        if (faviconFile) {
          this.seoService.changeIcon(faviconFile, environment.s3_url);
        }

        const website_title = company?.website_title || '';
        this.seoService.website_title = this.seoService.getFormattedTitle(
          website_title,
          company,
        );
        const website_description: string =
          user.company?.website_description || '';
        this.title.setTitle(website_title);
        this.seoService.setOneSeoTag('title', website_title);
        this.seoService.setOneSeoTag('description', website_description);

        const website_url: string = company?.company_url || '';
        this.seoService.setCanonicalURL(website_url);
        this.seoService.setSiteIdentity(company);

        if (company?.theme_css) {
          const companyName = company?.company_name || '';
          this.themeService.setTheme(
            companyName.replace(/\s+/g, ''),
            company.theme_css,
          );
        }
      });
  }
}
